import { DownloadIcon, LinkIcon, ViewIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Link,
  Text,
  Image,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Layout from "../../components/app/Layout";
import BaseContainer from "../../components/form/BaseContainer";
import { DropdownInput } from "../../components/form/controls/DropdownInput";
import { Loading } from "../../components/shared/Loading";
import { ArchivePreviewModal } from "../../components/modal/ArchivePreviewModal";
import { IntegrationItem } from "../../models/integration-item.model";
import { IntegrationService } from "../../service/integration.service";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

const baseContainerStyles = {
  flexGrow: "1",
  borderRadius: "2px",
  minH: { base: "calc(100vh - 80px - 24px)" },
  paddingBottom: "50px",
  minW: "95%",
};

const integrationService = new IntegrationService();

export const Archive: React.FC<any> = (props: any) => {
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [repoDropdown, setRepoDropdown] = useState([] as string[]);

  const [integrations, setIntegrations] = useState([] as IntegrationItem[]);
  const [selectedRepo, setRepoSelected] = useState({} as IntegrationItem);
  const [files, setFiles] = useState({} as any);
  const [downloadLink, setDownloadLink] = useState("");
  const [activeValue, setActiveValue] = useState("");

  const integrationWorkflows = useSelector(
    (state: RootState) => state.workflows
  );

  const init = async () => {
    let activeIntegrations = integrationWorkflows.activeIntegrations;

    if (!activeIntegrations?.length) {
      activeIntegrations = await fetchIntegrations();
    }

    setIntegrations(activeIntegrations);
    setRepoDropdown(activeIntegrations.map((x) => x.friendlyName));
  };

  const fetchIntegrations = async () => {
    setLoading(true);
    const integrations = await integrationService
      .getWorkflowsWithRuns({
        limit: 1,
      });

    setLoading(false);
    return integrations;
  };

  const handleDropdownChange = async (dropdownIndex: number) => {
    reset();
    const friendlyName = repoDropdown[+dropdownIndex];

    const item = integrations.find((x) => x.friendlyName === friendlyName);
    setRepoSelected(item);

    await integrationService
      .getRepositoryFileList(item.repoName, item.name)
      .then((files) => {
        if (Array.isArray(files.log) && files.log.length > 0) {
          files.log.sort((a, b) => {
            return new Date(b.split('.zip')[0]).getTime() - new Date(a.split('.zip')[0]).getTime();
          });
        
          console.log(files.log);
        }
        

        setFiles(files);
      })
      .finally(() => {
      });
  };

  const handleFileClick = async (filename: string) => {
    const url = await integrationService.getArchiveFileDownloadLink(
      selectedRepo.repoName,
      selectedRepo.name,
      filename
    );

    setActiveValue(filename);
    setDownloadLink(url);
  };

  const reset = () => {
    setRepoSelected({} as IntegrationItem);
    setFiles({} as any);
    setDownloadLink("");
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Layout>
      <Loading loading={loading} />
      <BaseContainer styles={baseContainerStyles}>
        <Box mb="28px">
          <Text
            fontSize="10px"
            fontWeight="500"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="gray.400"
            mb="6px"
          >
            Operations
          </Text>
          <Text fontWeight="500" fontSize="22px" letterSpacing="-0.02em">
            Archive & Logs
          </Text>
        </Box>
        <Flex flexDir="column" gap="26px">
          <Box>
            <StepTitle step={1} text="Select the integration" />
            <DropdownInput
              options={repoDropdown}
              onChange={(e) => handleDropdownChange(e.target.value)}
            ></DropdownInput>
          </Box>

          <Box hidden={!selectedRepo?.id}>
            <StepTitle step={2} text="Select files" />
            <Box
              border="1px solid"
              borderColor="gray.600"
              p="20px"
              borderRadius="2px"
              maxHeight="300px"
              overflowY="auto"
              bg="gray.800"
            >
              {!loading ? (
                !!Object.keys(files)?.length ? (
                  <FileTree
                    data={files}
                    activeValue={activeValue}
                    onClick={(file: string) => handleFileClick(file)}
                  />
                ) : (
                  <Text textAlign="center" color="gray.300" fontSize="13px">
                    No archived files available at this time.
                  </Text>
                )
              ) : (
                <Loading loading={loading} />
              )}
            </Box>
          </Box>

          <Box hidden={!downloadLink?.length} mb="16px">
            <StepTitle step={3} text="Download or Preview" />
            <Box
              p="20px"
              borderRadius="2px"
              border="1px dashed"
              borderColor="gray.600"
              _hover={{ borderColor: "brand.500" }}
              transition="border-color 0.15s"
            >
              <Flex
                flexDir="column"
                cursor="pointer"
                onClick={() => window.open(downloadLink, "_blank")}
                textAlign="center"
                alignItems="center"
                gap="12px"
              >
                <DownloadIcon fontSize="20px" color="brand.500" />
                <Text fontSize="13px" color="gray.300">
                  Click to download
                </Text>
                <Text fontWeight="500" fontFamily="mono" fontSize="13px">
                  {activeValue}
                </Text>
              </Flex>

              <Box
                borderTop="1px solid"
                borderColor="gray.600"
                my="20px"
              />

              <Flex
                flexDir="column"
                cursor="pointer"
                onClick={onPreviewOpen}
                textAlign="center"
                alignItems="center"
                gap="12px"
              >
                <ViewIcon fontSize="20px" color="brand.500" />
                <Text fontSize="13px" color="gray.300">
                  Preview
                </Text>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </BaseContainer>

      <ArchivePreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        filename={activeValue}
        downloadLink={downloadLink}
        repo={selectedRepo.repoName}
        workflow={selectedRepo.name}
      />
    </Layout>
  );
};

const StepTitle = (props: { step: 1 | 2 | 3; text: string }) => {
  const { step, text } = props;

  return (
    <Flex
      flexDir="row"
      my="10px"
      fontSize="13px"
      fontWeight="500"
      letterSpacing="0.04em"
      textTransform="uppercase"
      alignItems="center"
      color="gray.200"
    >
      <Box
        textAlign="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="22px"
        h="22px"
        borderRadius="2px"
        mr="10px"
        bg="brand.500"
        color="brand.100"
        fontSize="12px"
        fontFamily="mono"
        fontWeight="600"
      >
        {step}
      </Box>
      {text}
    </Flex>
  );
};

const FileTree = ({ data, onClick, activeValue }) => {
  return (
    <Flex flexDir="column">
      {Object.keys(data).map((key) => {
        const value = data[key];
        return (
          <Flex key={key}>
            {typeof value === "object" ? (
              <div>
                <Flex flexDir="row" gap="4px" mt="8px">
                  <Link as={LinkIcon} mt="4px" w="14px" h="14px" /> {key}
                </Flex>
                <FileTree
                  data={value}
                  onClick={onClick}
                  activeValue={activeValue}
                />
              </div>
            ) : (
              <Flex
                bg={activeValue === value ? "brand.50" : "transparent"}
                border={
                  activeValue === value
                    ? "1px solid"
                    : "1px solid transparent"
                }
                borderColor={activeValue === value ? "brand.600" : "transparent"}
                px="8px"
                py="4px"
                borderRadius="2px"
                flexDir="row"
                ml="8px"
                hidden={!value?.length}
                onClick={() => onClick(value)}
                _hover={{ bg: "gray.700" }}
                transition="all 0.15s"
              >
                <Flex
                  cursor="pointer"
                  fontWeight="400"
                  fontSize="13px"
                  flexDir="row"
                  gap="6px"
                  alignItems="center"
                  color={activeValue === value ? "brand.500" : "gray.200"}
                >
                  <Image src="/assets/file.png" w="14px" h="14px" opacity={0.7} />
                  {value}
                </Flex>
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
